import { PopoverPlacement } from "@idui/react-popover";
import { CalendarToday } from "@mui/icons-material";
import { ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import { StaticDateTimePicker } from "@mui/x-date-pickers";
import clsx from "clsx";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { CustomMuiPickersActionBar, FramerTooltip } from "@ryde/controls";
import {
  useClickOutside,
  useModalConfirm,
  useRidesActions,
  useRideStartEndEdit,
} from "@ryde/redux";
import {
  ConfirmResult,
  DATE_FORMAT_DMY,
  errors,
  formatDatetime,
  isDateInCalendarRange,
  isValidDateTime,
  Log,
  onActivateKeyDown,
  RideCrudModeKey,
  RideFormFields,
  rideStartEndPositionAtom,
  rideStartEndPositionSelector,
  topCalendarDatesAtomFamily,
  useFormats,
  useHasPermissions,
  useTimezone,
  type TRideDtoStartEndKey,
} from "@ryde/shared";

import {
  KanbanItemStyledTimeEditInputWrapper,
  KanbanTimeEditFieldStyled,
} from "../rides.kanban.item.styled";

const POPOVER_Y_OFFSET = 10;

interface IKanbanDateBox {
  timeWithDate: TDateISO | null | undefined;
}

interface IKanbanDateBoxEditable extends IKanbanDateBox {
  rideId: number;
  type: "IN" | "OUT";
  timeIsEditable: boolean;
}

export const KanbanDateBox: FC<IKanbanDateBoxEditable> = ({
  rideId: currentRideId,
  timeIsEditable,
  timeWithDate,
  type,
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<Dayjs | null>(null);
  const [hasError, setHasError] = useState(false);
  const prevTimeWithDateRef = useRef<Dayjs | null>(null);
  const { isFieldOpened, iconAnchorElement, rideId, fieldName } =
    useRecoilValue(rideStartEndPositionSelector);
  const setRideStartEndPosition = useSetRecoilState(rideStartEndPositionAtom);
  const { updateRideStartEnd } = useRideStartEndEdit(currentRideId);
  const { confirmDialogResult } = useModalConfirm();
  const { timezone } = useTimezone();
  const { isAMPM, timeFormat } = useFormats();
  const { t } = useTranslation();

  const { hasPermissions } = useHasPermissions({
    entity: "ride",
    action: "update",
  });

  const isDropoff = type === "OUT";
  const StartEndIcon = isDropoff
    ? SVG_MUI.BriefcaseOutline2SVG
    : SVG_MUI.HomeOutline2SVG;
  const fieldNameResolved: TRideDtoStartEndKey = isDropoff
    ? RideFormFields.rideEnd
    : RideFormFields.rideStart;
  const isEditMode =
    isFieldOpened &&
    rideId === currentRideId &&
    fieldName === fieldNameResolved;

  useEffect(() => {
    const val = timeWithDate ? dayjs.utc(timeWithDate) : null;
    setLocalValue(val);
    prevTimeWithDateRef.current = val;
  }, [timeWithDate]);

  const handleTimeFieldBlur = useEvent(async () => {
    if (prevTimeWithDateRef.current?.isSame(localValue)) {
      return;
    }

    const isValid = isValidDateTime(localValue) ?? false;
    if (isValid) {
      setHasError(false);
      const confirmationResult = await confirmDialogResult({
        title: t("general.UPDATE_CONFIRM"),
        message: t("ride.UPDATE_CONFIRM_TIME_MESSAGE"),
      });
      if (confirmationResult !== ConfirmResult.OK) {
        Log.debug("Time updating canceled by user");
        setLocalValue(prevTimeWithDateRef.current);
        return;
      }

      const isSuccess = await updateRideStartEnd(localValue, fieldNameResolved);

      if (isSuccess) {
        prevTimeWithDateRef.current = localValue;
        Log.debug("Update time data", localValue, "is valid", isValid);
      } else {
        Log.debug("Update time data was failed");
        setLocalValue(prevTimeWithDateRef.current);
      }
    } else {
      Log.warnAndToast(t(errors.ride.invalidRideDate));
      setHasError(true);
    }
  });

  const setIsFieldOpened = useEvent(() => {
    if (
      !timeIsEditable ||
      !hasPermissions ||
      !currentRideId ||
      iconAnchorElement
    ) {
      return;
    }

    setRideStartEndPosition(prev => {
      return {
        ...prev,
        fieldName: fieldNameResolved,
        rideId: currentRideId,
        isFieldOpened: true,
      };
    });
  });

  const onIconClickHandler = useEvent(event => {
    event.preventDefault();
    event.stopPropagation();
    setIsPopupOpen(false);

    if (hasPermissions && timeIsEditable) {
      const target = event.currentTarget as HTMLSpanElement;
      const rect = target?.getBoundingClientRect();

      const { x, y } = rect ?? {};

      setRideStartEndPosition(prev => ({
        ...prev,
        x,
        y,
        isFieldOpened: true,
        rideId: currentRideId,
        fieldName: fieldNameResolved,
        iconAnchorElement: event.currentTarget,
      }));
    }
  });

  const onClickOutside = useEvent(() => {
    const isThisPanelOpen =
      isFieldOpened &&
      rideId === currentRideId &&
      fieldName === fieldNameResolved &&
      !iconAnchorElement;
    if (isThisPanelOpen) {
      setRideStartEndPosition(prev => ({
        ...prev,
        isFieldOpened: false,
        rideId: null,
        fieldName: null,
        iconAnchorElement: null,
      }));
    }
    setIsPopupOpen(false);
  });

  const { clickRef } = useClickOutside<HTMLDivElement>({
    handleClose: onClickOutside,
  });

  const formattedValue = formatDatetime({
    dateTimeISO: timeWithDate,
    dateFormat: DATE_FORMAT_DMY,
    timeFormat,
    timezone,
  });

  return (
    <div
      ref={clickRef}
      onMouseEnter={() => setIsPopupOpen(true)}
      onMouseLeave={() => setIsPopupOpen(false)}
    >
      <KanbanItemStyledTimeEditInputWrapper
        className={clsx({
          error: hasError,
          wrapper_date_in: true,
          ampm: isAMPM,
          isDropoff,
        })}
        onClick={setIsFieldOpened}
      >
        <div
          onClick={onIconClickHandler}
          onKeyDown={onActivateKeyDown(onIconClickHandler)}
          role="button"
          tabIndex={0}
          aria-label={t("general.editTime")}
        >
          <StartEndIcon
            className={clsx("startEndIcon", { isEditMode, isDropoff })}
          />

          <CalendarToday
            className={clsx("calendarIcon", { isEditMode, isDropoff })}
          />
        </div>

        <FramerTooltip
          content={formattedValue}
          placement={PopoverPlacement.top}
          isOpen={isPopupOpen && !isEditMode}
        >
          <KanbanTimeEditFieldStyled
            $isDropoff={isDropoff}
            $isEditMode={isEditMode}
            $isAMPM={isAMPM}
            value={localValue}
            onChange={setLocalValue}
            onBlur={handleTimeFieldBlur}
            ampm={isAMPM}
            timezone={timezone}
            readOnly={!timeIsEditable || !hasPermissions}
            slotProps={{
              textField: {
                variant: "standard",
                size: "small",
                error: hasError,
                slotProps: {
                  input: {
                    disableUnderline: true,
                  },
                  htmlInput: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention -- aria-label is the required HTML attribute name
                    "aria-label": isDropoff ? "End time" : "Start time",
                  },
                },
              },
            }}
          />
        </FramerTooltip>
        {isEditMode && (
          <KanbanDateBoxEditablePopper timeWithDate={timeWithDate} />
        )}
      </KanbanItemStyledTimeEditInputWrapper>
    </div>
  );
};

export const KanbanDateBoxEditablePopper: FC<IKanbanDateBox> = ({
  timeWithDate,
}) => {
  const { fetchRides } = useRidesActions();
  const { timezone } = useTimezone();
  const calendarDates = useRecoilValue(
    topCalendarDatesAtomFamily(RideCrudModeKey.uiUnit),
  );
  const parsedInitial = timeWithDate ? dayjs.utc(timeWithDate) : null;
  const [value, setValue] = useState<Dayjs | null>(parsedInitial);

  const setRideStartEndPosition = useSetRecoilState(rideStartEndPositionAtom);
  const { isFieldOpened, fieldName, rideId, iconAnchorElement } =
    useRecoilValue(rideStartEndPositionSelector);
  const { updateRideStartEnd } = useRideStartEndEdit(rideId);
  const { isAMPM } = useFormats();

  useEffect(() => {
    setValue(timeWithDate ? dayjs.utc(timeWithDate) : null);
  }, [timeWithDate]);

  const handleClosePopover = useEvent((): void => {
    setRideStartEndPosition(prev => ({
      ...prev,
      x: 0,
      y: 0,
      rideId: null,
      fieldName: null,
      isFieldOpened: false,
      iconAnchorElement: null,
    }));
  });

  const setDate = useEvent(
    async (valueUtc: dayjs.Dayjs | null): Promise<void> => {
      if (fieldName) {
        const isSuccess = await updateRideStartEnd(valueUtc, fieldName);

        if (isSuccess && !isDateInCalendarRange(valueUtc, calendarDates)) {
          fetchRides();
        }
      }
    },
  );

  return (
    <Popper
      open={Boolean(iconAnchorElement && isFieldOpened)}
      anchorEl={iconAnchorElement as HTMLElement}
      placement={"bottom-start"}
      popperOptions={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, POPOVER_Y_OFFSET],
            },
          },
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 10,
              altAxis: true,
              tether: true,
            },
          },
        ],
      }}
      transition
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={350}>
          <Paper elevation={8}>
            <ClickAwayListener onClickAway={handleClosePopover}>
              <StaticDateTimePicker
                value={dayjs.utc(value)}
                timezone={timezone}
                onChange={setValue}
                onAccept={setDate}
                slots={{
                  actionBar: props => (
                    <CustomMuiPickersActionBar
                      customOnCancel={handleClosePopover}
                      customOnAccept={handleClosePopover}
                      {...props}
                    />
                  ),
                }}
                ampm={isAMPM}
              />
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};
